import { useId, useState } from 'react'
import { Bottone } from './base.tsx'

/**
 * Il messenger sul telefono con Element Classic (decisione di Davide dell'1/11/2026): due comandi,
 * iPhone e Android, e sotto i passi della piattaforma scelta. Sta in cima alla pagina Messenger aperta
 * dal telefono, nella sua guida in fondo e nel riquadro «App per il telefono» del profilo.
 *
 * Element X no: entra solo con l'accesso OIDC, che il server del messenger non ha. Il personale entra
 * con il CAS del gestionale (apps/api/src/rotte/messenger.ts), e i passi seguono quello che succede
 * davvero: Element manda il browser del telefono al server Matrix, che lo gira a /api/cas/login; senza
 * una sessione completa in quel browser il gestionale mostra l'accesso su /messenger/accesso e poi torna
 * da solo al CAS (RitornoAlMessenger in pagine/Messaggi.tsx); il CAS rimanda al server con il ticket.
 * Il server chiede di confermare l'app, in inglese, perche' fra i client ammessi senza conferma c'e'
 * solo Element web (`sso.client_whitelist` in infra/messenger/synapse/homeserver.yaml.modello), poi
 * riapre Element.
 */

type Piattaforma = 'iphone' | 'android'

/** `store`: con la preposizione, per «Element Classic dall'App Store». */
const APP: Record<Piattaforma, { comando: string; store: string; indirizzo: string }> = {
  iphone: {
    comando: 'Element Classic per iPhone', store: 'dall’App Store',
    indirizzo: 'https://apps.apple.com/it/app/id1083446067',
  },
  android: {
    comando: 'Element Classic per Android', store: 'da Google Play',
    indirizzo: 'https://play.google.com/store/apps/details?id=im.vector.app',
  },
}

/** `indirizzo`: il server del messenger, come la pagina Messenger lo mostra (GET /auth/io). */
export function IstruzioniElement({ indirizzo }: { indirizzo: string | null }) {
  const [aperta, setAperta] = useState<Piattaforma | null>(null)
  // La pagina Messenger dal telefono lo mostra due volte, in cima e nella guida: identificativi propri.
  const id = useId()
  const piattaforme: Piattaforma[] = ['iphone', 'android']

  return (
    <div className="element-istruzioni">
      <div className="element-scelta">
        {piattaforme.map((p) => (
          <Bottone key={p} dimensione="piccolo" variante={aperta === p ? 'primario' : 'secondario'}
                   aria-expanded={aperta === p} aria-controls={`${id}-${p}`}
                   onClick={() => setAperta(aperta === p ? null : p)}>
            {APP[p].comando}
          </Bottone>
        ))}
      </div>
      {piattaforme.map((p) => (
        <ol key={p} id={`${id}-${p}`} className="messenger-passi element-passi" hidden={aperta !== p}>
          <li>
            Installa <a href={APP[p].indirizzo} target="_blank" rel="noopener noreferrer">Element Classic
            {' '}{APP[p].store}</a>. <strong>Non Element X</strong>: con l’accesso del gestionale non entra.
          </li>
          <li>
            Apri Element Classic, scegli di accedere e cambia il server. Indirizzo del server:{' '}
            {indirizzo
              ? <span className="mono messenger-indirizzo">{indirizzo}</span>
              : <span className="tenue">compare qui quando il messenger è attivo</span>}.
          </li>
          <li>
            Tocca «Gestionale CIDIS», senza scrivere nome utente e password nei campi di Element.
            {p === 'iphone' && ' Se l’iPhone chiede di consentire a Element di usare il sito per l’accesso, tocca «Continua».'}
          </li>
          <li>
            Nel browser del telefono si apre l’accesso del gestionale: entra con nome utente, password e, se l’hai
            attivato, il codice del secondo fattore. Se in quel browser sei già entrato nel gestionale, questo
            passo non c’è.
          </li>
          <li>
            Il messenger chiede di confermare l’accesso di Element, con una pagina in inglese: tocca «Continue».
            Element si riapre, già collegato.
          </li>
          <li>
            Element chiede di confermare il nuovo dispositivo. Apri il messenger dove sei già entrato la prima
            volta, di solito il gestionale sul computer, pagina «Messenger»: lì compare la richiesta di verificare
            il nuovo accesso. Accettala; sul telefono e sul computer compaiono le stesse emoji: se coincidono,
            confermalo su tutti e due. Senza un altro dispositivo collegato, sul telefono scegli la chiave di
            recupero e scrivila. Senza conferma i messaggi vecchi restano illeggibili.
          </li>
        </ol>
      ))}
    </div>
  )
}
